// Page Visibility API + AFK タイマーで「退室中」状態を判定して WS に push する共通モジュール。
// 全ページ (/、/club、/lite) が同じ ChatRoom DO に visible 状態を報告する。
//
// 「退室中」(= away) の trigger:
//   1. document.visibilityState === "hidden" (= タブ裏 / 画面ロック / 最小化)
//   2. 一定時間 (= AFK_THRESHOLD_MS = 5 分) 入力イベントなし (= 席離れ)
//
// 入力イベントの定義: mousemove / keydown / touchstart / pointerdown / wheel。
//   - これらのいずれかが発火したら last activity time を更新
//   - 30 秒ごとに「いま AFK threshold を超えたか」をチェック
//
// WS への push は重複防止: 最後に送った値と同じなら送らない (= broadcast 量削減)。

const AFK_THRESHOLD_MS = 5 * 60_000; // 5 分
const AFK_CHECK_INTERVAL_MS = 30_000; // 30 秒
const ACTIVITY_EVENTS: ReadonlyArray<keyof DocumentEventMap> = [
  "mousemove",
  "keydown",
  "touchstart",
  "pointerdown",
  "wheel",
] as const;

export interface VisibilityTracker {
  /** WS が open になったら呼ぶ。現在の visible 状態を即送る (= server に snapshot)。 */
  notifyWsOpen(): void;
  /** 入室クリーンアップ。listener / timer を解除する。 */
  stop(): void;
}

/** WS を渡して visibility tracker を起動。
 *  send fn は「WS が open ならメッセージを送る」だけ。caller 側で OPEN check 込み。 */
export function startVisibilityTracker(
  sendFn: (msg: { type: "VISIBILITY"; visible: boolean }) => void,
): VisibilityTracker {
  let lastActivity = Date.now();
  let lastSent: boolean | null = null;

  const isVisibleNow = (): boolean => {
    // タブ裏なら away、それ以外は AFK しきい値未満なら visible
    if (typeof document === "undefined") return true;
    if (document.visibilityState === "hidden") return false;
    return Date.now() - lastActivity < AFK_THRESHOLD_MS;
  };

  const pushIfChanged = (): void => {
    const v = isVisibleNow();
    if (v === lastSent) return;
    lastSent = v;
    sendFn({ type: "VISIBILITY", visible: v });
  };

  const onActivity = (): void => {
    lastActivity = Date.now();
    // AFK 復帰判定: 今 away なら即 visible に戻す
    if (lastSent === false && document.visibilityState !== "hidden") {
      pushIfChanged();
    }
  };

  const onVisibilityChange = (): void => {
    if (document.visibilityState === "visible") {
      // タブ手前に戻った瞬間は AFK timer もリセット (= 「戻ってきた」)
      lastActivity = Date.now();
    }
    pushIfChanged();
  };

  for (const ev of ACTIVITY_EVENTS) {
    document.addEventListener(ev, onActivity, { passive: true });
  }
  document.addEventListener("visibilitychange", onVisibilityChange);
  const timer = setInterval(pushIfChanged, AFK_CHECK_INTERVAL_MS);

  return {
    notifyWsOpen(): void {
      // 接続直後に現在状態を送る (= server の default true と齟齬がないように)。
      // lastSent をリセットして強制送信する。
      lastSent = null;
      pushIfChanged();
    },
    stop(): void {
      for (const ev of ACTIVITY_EVENTS) {
        document.removeEventListener(ev, onActivity);
      }
      document.removeEventListener("visibilitychange", onVisibilityChange);
      clearInterval(timer);
    },
  };
}
